import { createSelector } from 'reselect'
import { DateTime } from 'luxon'
import type { ReduxState } from '@/utils/typeHelpers'
import { isoToUTCDateTime } from '@/utils/dateHelpers'
import { quarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { withDerivedAnswers } from './questions/registry'

const selectRawAnswers = (state: ReduxState) => state.compliance.answers
/** Raw answers plus any DERIVED_ANSWERS (e.g. hiring_plan inferred from
 *  practice_size) — the version every reader should use. */
export const selectAnswers = createSelector(
  selectRawAnswers,
  withDerivedAnswers
)
export const selectSectionIndex = (state: ReduxState) =>
  state.compliance.sectionIndex
export const selectLicenses = (state: ReduxState) => state.compliance.licenses
export const selectReport = (state: ReduxState) => state.compliance.report
export const selectRecommendedOffers = (state: ReduxState) =>
  state.compliance.recommendedOffers
export const selectLicenseOptions = (state: ReduxState) =>
  state.compliance.licenseOptions

/**
 * The next `count` real IRS quarterly payment deadlines, soonest first, from
 * `quarterly_tax_estimate_details` — the same rows QTE runs on.
 *
 * Preferred over computing nominal 15ths: `irsPaymentDueAt` already carries the
 * shift to the next business day when a deadline lands on a weekend or holiday.
 * Returns fewer than `count` (or none) when the table has no future rows, so
 * callers must handle a short list rather than assume one.
 *
 * `asOf` defaults to now and exists so the cutoff is testable. Note the result
 * is memoised on its inputs, so a session left open across a deadline's
 * midnight keeps the list it computed on load — acceptable for a funnel that
 * lasts minutes, and the reason the cutoff is a parameter rather than only a
 * clock read.
 */
export const selectNextQuarterlyPaymentDueDates = createSelector(
  quarterlyTaxEstimateDetails,
  (_: unknown, count: number) => count,
  (_s: unknown, _c: unknown, asOf?: DateTime) => asOf,
  (details, count, asOf) => {
    // Compared as calendar dates, not instants. `irsPaymentDueAt` is date-only
    // stored at UTC midnight, while a viewer's `startOf('day')` is their own
    // midnight — west of UTC that lands *after* the stored instant, which
    // dropped a deadline on its own day for every US timezone. Comparing
    // ISO dates asks the question actually being asked ("is this deadline's
    // date still today or later, where the reader is") and holds in any zone.
    const today = (asOf ?? DateTime.now()).toISODate() ?? ''
    return Object.values(details)
      .map((detail) => ({
        quarter: detail.taxQuarter,
        dueAt: isoToUTCDateTime(detail.irsPaymentDueAt),
      }))
      .filter(
        ({ dueAt }) => dueAt.isValid && (dueAt.toISODate() ?? '') >= today
      )
      .sort((a, b) => a.dueAt.toMillis() - b.dueAt.toMillis())
      .slice(0, count)
  }
)
